import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ProductDto } from "#modules/products/infra/fetchProducts.api";
import { getProductTypeToRender } from "#shared/helpers/ProductTypeHelper";
import { Icon } from "#shared/icons/Icon";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { EditableTable } from "#shared/view/components/EditableTable/EditableTable";
import { EditableTableCell } from "#shared/view/components/EditableTable/EditableTableCell";
import { Group } from "#shared/view/components/Group";
import { formatPrice, ProductTypeType } from "shared";

type ProductsSettingsTableProps = {
  data: ProductDto[];
  onUpdateCell: (rowId: string, columnId: string, value: unknown) => void;
  onAddRow: () => void;
  onRemoveRow: (rowId: string) => void;
};

export const ProductsSettingsTable = ({
  data,
  onUpdateCell,
  onAddRow,
  onRemoveRow,
}: ProductsSettingsTableProps) => {
  const { t } = useTranslation();

  const productTypeOptions: { label: string; value: ProductTypeType }[] = useMemo(
    () => [
      { label: t("qrl.productType.CONSUMABLE"), value: "CONSUMABLE" },
      { label: t("qrl.productType.PACKING"), value: "PACKING" },
      { label: t("qrl.productType.OUTSOURCING"), value: "OUTSOURCING" },
    ],
    [t],
  );

  const defaultColumn: Partial<ColumnDef<ProductDto>> = {
    cell: ({ getValue, row, column: { id }, table }) => (
      <EditableTableCell
        initialValue={getValue()}
        rowId={row.id}
        columnId={String(id)}
        table={table}
      />
    ),
  };

  const columns = useMemo<ColumnDef<ProductDto>[]>(
    () => [
      {
        accessorKey: "type",
        header: t("common.type"),
        meta: { width: "20%" },
        cell: ({ getValue, row, table }) => (
          <EditableTableCell
            initialValue={getValue()}
            rowId={row.id}
            columnId="type"
            table={table}
            type="select"
            options={productTypeOptions}
            formatDisplayValue={(value) => getProductTypeToRender(value as ProductTypeType)}
          />
        ),
      },
      {
        accessorKey: "name",
        header: t("common.name"),
        meta: { width: "40%" },
      },
      {
        accessorKey: "price",
        header: t("common.price"),
        cell: ({ getValue, row, table }) => (
          <EditableTableCell
            initialValue={getValue()}
            rowId={row.id}
            columnId="price"
            type="price"
            table={table}
            formatDisplayValue={(value) => formatPrice((value as number | null) ?? 0)}
          />
        ),
        meta: { width: "30%" },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <Group gap="xs" justify="end">
            <ActionIcon
              onClick={() => onRemoveRow(row.id)}
              variant="subtle"
              size="sm"
              aria-label={`Remove row ${row.index}`}
            >
              <Icon name="trash" size={14} />
            </ActionIcon>
          </Group>
        ),
        meta: { width: "10%" },
      },
    ],
    [onRemoveRow, productTypeOptions, t],
  );

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [data]);

  return (
    <EditableTable
      data={sortedData}
      columns={columns}
      defaultColumn={defaultColumn}
      getRowId={(row) => row.id}
      onUpdateCell={onUpdateCell}
      onAddRow={onAddRow}
      pageSize={10}
    />
  );
};
